import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useCopyToClipboard } from '../components/CopyButton';
import { ErrorDisplay } from '../components/ErrorDisplay';
import { Layout } from '../components/Layout';
import { WizardSteps, quickShareSteps, storeSteps } from '../components/WizardSteps';
import { Button, Input, Select } from '../components/ui';
import { SparkleIcon, DropArrowIcon, CheckIcon, LockIcon } from '../components/Icons';
import { uploadFile, getShareableLink, getShareableLinkEncrypted } from '../services/swarm/upload';
import { useAuth } from '../contexts/AuthContext';
import {
  getAccounts,
  getAccount,
  verifyPassword,
  addMessage,
} from '../services/storage';
import { encryptFile, hexToBytes } from '../services/swarm/encryption';
import { logger } from '../utils/logger';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '../shared/utils/format';
import { BrowserCryptoProvider } from '@fairdrop/core/crypto/browser';
import {
  encryptSwarmRef,
  serializeEncryptedSwarmRef,
} from '@fairdrop/core/crypto';
import { writeToInbox, findNextSlot } from '../services/swarm/gsoc';
import { resolveRecipient, ENS_DOMAIN } from '../services/ens';
import { DEFAULT_ESCROW_CHAIN_ID, SUPPORTED_CHAINS } from '../services/wallet/keypair-wallet';
import { createEscrow as createOnChainEscrow, generateEncryptionKey } from '../services/escrow/escrow';
import type { Account, Message } from '../types';

// Singleton crypto provider for encrypted Swarm references
const cryptoProvider = new BrowserCryptoProvider();

type UploadMode = 'send' | 'store' | 'quick';
type UploadStep = 'select' | 'form' | 'confirm' | 'uploading' | 'success';

export function Upload() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { username: activeSubdomain } = useAuth();

  const mode = (searchParams.get('mode') || 'send') as UploadMode;
  const isEncrypted = mode === 'send' || mode === 'store';

  // Get file from router state (passed from Home)
  const initialFile = (location.state as { file?: File } | null)?.file || null;

  // State - start at confirm if file was passed, otherwise select
  const [step, setStep] = useState<UploadStep>(() => {
    if (initialFile) {
      return isEncrypted ? 'form' : 'confirm';
    }
    return 'select';
  });
  const [file, setFile] = useState<File | null>(initialFile);
  const [isDragging, setIsDragging] = useState(false);

  // Display file is just the selected file (no more mock fallback)
  const displayFile = file;

  // Mailbox state
  const [mailboxOptions, setMailboxOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedMailbox, setSelectedMailbox] = useState('');
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Recipient state (for Send mode)
  const [recipient, setRecipient] = useState('');
  const [recipientAccount, setRecipientAccount] = useState<Account | null>(null);
  const [recipientFound, setRecipientFound] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);

  // Escrow state (optional for Send mode)
  const [escrowEnabled, setEscrowEnabled] = useState(false);
  const [escrowPrice, setEscrowPrice] = useState('0.01');
  const [escrowExpiry, setEscrowExpiry] = useState('7');
  const [escrowId, setEscrowId] = useState<string | null>(null);

  // Re-run recipient lookup when returning to form with existing value
  useEffect(() => {
    if (step === 'form' && recipient && !recipientAccount && !isLookingUp) {
      // Trigger lookup for existing recipient value
      lookupRecipient(recipient);
    }
  }, [step, recipient, recipientAccount, isLookingUp]);

  // Recipient lookup function (used by both onChange and useEffect)
  const lookupRecipient = async (value: string) => {
    if (!value.trim()) {
      setRecipientFound(false);
      setRecipientAccount(null);
      return;
    }

    setIsLookingUp(true);

    // First, check local storage (for locally created accounts)
    const localAccount = getAccount(value.toLowerCase());
    if (localAccount) {
      setRecipientAccount(localAccount);
      setRecipientFound(true);
      setIsLookingUp(false);
      return;
    }

    // If not found locally, check ENS
    try {
      const result = await resolveRecipient(value);
      if (result.publicKey) {
        // Create a partial Account from ENS data for encryption
        const ensAccount: Account = {
          subdomain: result.ensName?.replace(`.${ENS_DOMAIN}`, '') || value,
          publicKey: result.publicKey,
          privateKey: '', // Not available for ENS-resolved accounts
          passwordHash: '', // Not available for ENS-resolved accounts
          inboxParams: result.inboxParams || undefined,
          created: Date.now(),
        };
        setRecipientAccount(ensAccount);
        setRecipientFound(true);
      } else {
        setRecipientFound(false);
        setRecipientAccount(null);
      }
    } catch (error) {
      logger.error('[Upload] ENS lookup failed:', error);
      setRecipientFound(false);
      setRecipientAccount(null);
    } finally {
      setIsLookingUp(false);
    }
  };

  // Progress state
  const [progress, setProgress] = useState(0);
  const [downloadLink, setDownloadLink] = useState('');
  const [swarmHash, setSwarmHash] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  // Load mailboxes on mount and check for active session
  useEffect(() => {
    const accounts = getAccounts();
    const options = Object.keys(accounts).map(subdomain => ({
      value: subdomain,
      label: subdomain,
    }));
    setMailboxOptions(options);

    // Check if user is already logged in (from auth context)
    if (activeSubdomain) {
      const activeAccount = getAccount(activeSubdomain);
      if (activeAccount) {
        // User is already authenticated - use their account
        setCurrentAccount(activeAccount);
        setSelectedMailbox(activeSubdomain);
        logger.debug('[Upload] Using active session:', activeSubdomain);
      }
    } else if (options.length > 0) {
      setSelectedMailbox(options[0].value);
    }
  }, [activeSubdomain]);

  // Reset state when mode changes OR when navigating away from success page
  // Use location.key which changes on every navigation
  const prevLocationKeyRef = useRef(location.key);
  const prevModeRef = useRef(mode);
  const prevStepRef = useRef(step);
  useEffect(() => {
    const locationChanged = prevLocationKeyRef.current !== location.key;
    const modeChanged = prevModeRef.current !== mode;

    // Reset if mode changed, or if location changed while ALREADY on success step
    // Use prevStepRef to check if we WERE on success (not if we just arrived)
    if (modeChanged || (locationChanged && prevStepRef.current === 'success')) {
      prevLocationKeyRef.current = location.key;
      prevModeRef.current = mode;
      prevStepRef.current = step;
      // Reset to initial state for new upload flow
      setFile(null);
      setStep('select');
      setProgress(0);
      setDownloadLink('');
      setSwarmHash('');
      setRecipient('');
      setRecipientAccount(null);
      setRecipientFound(false);
      setLoginError('');
      setPassword('');
      setUploadError(null);
      // Reset escrow state
      setEscrowEnabled(false);
      setEscrowPrice('0.01');
      setEscrowExpiry('7');
      setEscrowId(null);
    } else {
      // Just update refs without resetting
      prevLocationKeyRef.current = location.key;
      prevModeRef.current = mode;
      prevStepRef.current = step;
    }
  }, [mode, location.key, step]);

  // File selection handlers
  const handleFileSelect = useCallback((selectedFile: File) => {
    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setFileSizeError(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }
    setFileSizeError(null);
    setFile(selectedFile);
    // If user is already logged in (has currentAccount)
    if (currentAccount) {
      // For Store mode: skip form entirely, go to confirm
      if (mode === 'store') {
        setStep('confirm');
      } else if (mode === 'send') {
        // For Send mode: still need recipient, show form but skip password fields
        setStep('form');
      } else {
        setStep('confirm');
      }
    } else {
      setStep(isEncrypted ? 'form' : 'confirm');
    }
  }, [isEncrypted, currentAccount, mode]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) handleFileSelect(selectedFile);
  }, [handleFileSelect]);

  // Form handlers
  const handleRecipientChange = (value: string) => {
    setRecipient(value);
    lookupRecipient(value);
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!selectedMailbox) {
      setLoginError('Please select an account');
      return;
    }

    const account = getAccount(selectedMailbox);
    if (!account) {
      setLoginError('Account not found');
      return;
    }

    const isValid = await verifyPassword(password, account.passwordHash);
    if (!isValid) {
      setLoginError('Incorrect password');
      return;
    }

    // For send mode, verify recipient exists
    if (mode === 'send' && !recipientAccount) {
      setLoginError('Recipient not found');
      return;
    }

    setCurrentAccount(account);
    setStep('confirm');
  };

  // Real upload to Swarm
  const startUpload = async () => {
    const fileToUpload = file;

    if (!fileToUpload) {
      logger.error('No file to upload');
      return;
    }

    logger.debug('[Upload] Starting upload:', { mode, isEncrypted, hasAccount: !!currentAccount });
    setStep('uploading');
    setProgress(0);
    setUploadError(null); // Clear any previous errors

    try {
      let reference: string;
      let shareLink: string;

      if (isEncrypted && currentAccount) {
        // Encrypted upload (Send or Store mode)
        setProgress(5);

        // Determine recipient public key
        // For Store mode: encrypt with own public key
        // For Send mode: encrypt with recipient's public key
        const targetPublicKey = mode === 'send' && recipientAccount
          ? hexToBytes(recipientAccount.publicKey)
          : hexToBytes(currentAccount.publicKey);

        // Encrypt the file
        setProgress(10);
        const encrypted = await encryptFile(fileToUpload, targetPublicKey);
        setProgress(30);

        // Combine encrypted data into uploadable format:
        // [ephemeralPubKey (33 bytes)] [iv (12 bytes)] [ciphertext]
        const combined = new Uint8Array(
          encrypted.ephemeralPublicKey.length +
          encrypted.iv.length +
          encrypted.ciphertext.length
        );
        combined.set(encrypted.ephemeralPublicKey, 0);
        combined.set(encrypted.iv, encrypted.ephemeralPublicKey.length);
        combined.set(encrypted.ciphertext, encrypted.ephemeralPublicKey.length + encrypted.iv.length);

        // Create a Blob/File from encrypted data
        const encryptedBlob = new Blob([combined], { type: 'application/octet-stream' });
        const encryptedFile = new File([encryptedBlob], fileToUpload.name + '.encrypted', {
          type: 'application/octet-stream',
        });

        // Upload encrypted file
        reference = await uploadFile(encryptedFile, {
          onProgress: (p) => setProgress(30 + p * 0.7), // 30-100%
          onStatusChange: (status) => {
            if (status === 'complete') setProgress(100);
          },
        });

        // Save message to storage
        const message: Message = {
          reference,
          filename: fileToUpload.name,
          size: fileToUpload.size,
          from: currentAccount.subdomain,
          to: mode === 'send' ? recipient : undefined,
          timestamp: Date.now(),
          encrypted: true,
        };

        // Add to sender's sent messages
        addMessage(currentAccount.subdomain, 'sent', message);

        // For send mode, also add to recipient's received (local storage)
        if (mode === 'send' && recipientAccount) {
          addMessage(recipientAccount.subdomain, 'received', {
            ...message,
            from: currentAccount.subdomain,
          });

          // Write to recipient's GSOC inbox on Swarm so they can receive it
          if (recipientAccount.inboxParams) {
            try {
              logger.debug('[Upload] Writing to recipient GSOC inbox...');
              const nextSlot = await findNextSlot(recipientAccount.inboxParams);
              await writeToInbox(
                recipientAccount.inboxParams,
                nextSlot,
                {
                  reference,
                  filename: fileToUpload.name,
                  size: fileToUpload.size,
                  senderInfo: {
                    from: currentAccount.subdomain,
                    filename: fileToUpload.name,
                  },
                },
                { anonymous: false }
              );
              logger.debug('[Upload] ✅ Message written to recipient inbox at slot', nextSlot);
            } catch (inboxErr) {
              logger.error('[Upload] Failed to write to recipient inbox:', inboxErr);
              // Don't fail the upload - the file is still accessible via the link
            }
          } else {
            logger.debug('[Upload] Recipient has no inboxParams - using direct link only');
          }
        }

        // For store mode, add to stored
        if (mode === 'store') {
          addMessage(currentAccount.subdomain, 'stored', message);
        }

        // Generate share link
        if (mode === 'send' && recipientAccount) {
          // Encrypt the Swarm reference so only the recipient can locate the content
          const encryptedRef = await encryptSwarmRef(
            cryptoProvider,
            reference,
            targetPublicKey
          );
          const serializedRef = serializeEncryptedSwarmRef(encryptedRef);
          shareLink = getShareableLinkEncrypted(serializedRef, fileToUpload.name + '.encrypted');
        } else {
          // Store mode - no need to encrypt ref (encrypted to self)
          shareLink = getShareableLink(reference, fileToUpload.name + '.encrypted');
        }

        // Create escrow on-chain if enabled
        if (escrowEnabled && currentAccount.privateKey) {
          setProgress(95);
          try {
            logger.debug('[Upload] Creating on-chain escrow...');
            const escrowKey = generateEncryptionKey();
            const escrowResult = await createOnChainEscrow({
              swarmReference: reference,
              encryptionKey: escrowKey,
              priceEth: escrowPrice,
              expiryDays: parseInt(escrowExpiry, 10),
              sellerPrivateKey: currentAccount.privateKey,
            });
            setEscrowId(escrowResult.escrowId);
            logger.debug('[Upload] Escrow created:', escrowResult.escrowId);
          } catch (escrowErr) {
            logger.error('[Upload] Failed to create escrow:', escrowErr);
            // Don't fail the upload - the file is still uploaded
            // Show error but continue to success
          }
        }
      } else {
        // Quick share (unencrypted)
        reference = await uploadFile(fileToUpload, {
          onProgress: (p) => setProgress(p),
          onStatusChange: (status) => {
            if (status === 'uploading') setProgress(10);
            if (status === 'complete') setProgress(100);
          },
        });

        shareLink = getShareableLink(reference, fileToUpload.name);
      }

      setSwarmHash(reference);
      setDownloadLink(shareLink);

      // Track successful upload for H001 validation
      if (typeof window !== 'undefined' && (window as any).posthog) {
        (window as any).posthog.capture('file_uploaded', {
          mode,
          file_size: fileToUpload.size,
          encrypted: isEncrypted,
        });
      }

      setStep('success');
    } catch (error) {
      logger.error('[Upload] Upload failed:', { error, mode, step, errorCode: (error as any)?.code });

      // Check for stamp-related errors
      const errorCode = (error as any)?.code;
      const isStampError = errorCode === 'STAMP_ERROR' || errorCode === 'STAMP_UNAVAILABLE';

      logger.debug('[Upload] Error analysis:', { errorCode, isStampError, errorMessage: (error as Error)?.message });

      if (isStampError) {
        // Show detailed stamp error message
        const stampErrorMsg =
          'STAMP_ERROR:Due to many transfers, the postage stamp capacity is currently full. ' +
          'The stamp will be replenished as soon as possible. ' +
          'To upload immediately, you can create a new stamp yourself (see instructions below).';
        logger.debug('[Upload] Setting stamp error message');
        setUploadError(stampErrorMsg);
      } else {
        // Show generic error message
        const errorMessage = error instanceof Error ? error.message : 'Upload failed. Please try again.';
        logger.debug('[Upload] Setting generic error message:', errorMessage);
        setUploadError(errorMessage);
      }

      // Go back to confirm step so user can retry
      logger.debug('[Upload] Returning to confirm step');
      setStep('confirm');
      setProgress(0);
    }
  };

  const { copy, isCopied } = useCopyToClipboard();

  // Shorten hash for display (first 8 + last 8)
  const shortenHash = (hash: string) => {
    if (hash.length <= 20) return hash;
    return `${hash.slice(0, 8)}...${hash.slice(-8)}`;
  };

  // Determine wizard step number based on mode - sequential progression
  const getWizardStep = (): number => {
    if (mode === 'quick') {
      // Quick Share: 1 Select File, 2 Confirm, 3 Upload, 4 Summary
      switch (step) {
        case 'select': return 1;
        case 'confirm': return 2;
        case 'uploading': return 3;
        case 'success': return 4;
        default: return 1;
      }
    } else if (mode === 'store') {
      // Store: 1 Select File, 2 Encrypt (form), 3 Upload, 4 Summary
      switch (step) {
        case 'select': return 1;
        case 'form': return 2;
        case 'confirm': return 2;
        case 'uploading': return 3;
        case 'success': return 4;
        default: return 1;
      }
    } else {
      // Send Encrypted: 1 Select File, 2 Encrypt (form), 3 Add Recipients (confirm), 4 Upload, 5 Summary
      switch (step) {
        case 'select': return 1;
        case 'form': return 2;
        case 'confirm': return 3;
        case 'uploading': return 4;
        case 'success': return 5;
        default: return 1;
      }
    }
  };

  // Get page title based on mode
  const getTitle = (): string => {
    switch (mode) {
      case 'send': return 'Send Encrypted';
      case 'store': return 'Store File';
      case 'quick': return 'Quick Share';
      default: return 'Upload';
    }
  };

  // Render file selection step
  const renderSelectFile = () => (
    <div
      className={`content-centered dropzone ${isDragging ? 'dropzone-active' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <h1 className="heading-lg">{getTitle()}</h1>
      <p style={{ fontSize: 18, marginTop: 8, marginBottom: 40, opacity: 0.8 }}>
        {isEncrypted ? 'Your file will be encrypted before upload' : 'Share without encryption'}
      </p>

      <div
        className="file-action"
        onClick={() => fileInputRef.current?.click()}
        style={{ fontSize: 'var(--font-size-xl)' }}
      >
        <SparkleIcon className="file-action-icon" />
        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>select</span>
        <span style={{ margin: '0 8px' }}>or</span>
        <DropArrowIcon className="file-action-icon" />
        <span>drop a file</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      <p style={{ marginTop: 24, fontSize: 13, opacity: 0.6 }}>
        Maximum file size: {MAX_FILE_SIZE_MB}MB
      </p>

      {fileSizeError && (
        <p style={{ marginTop: 16, color: '#e74c3c', fontSize: 14 }}>
          {fileSizeError}
        </p>
      )}

      {isDragging && <div className="drop-indicator">Drop your file here</div>}
    </div>
  );

  // Handle recipient-only form submission (for logged-in users)
  const handleRecipientSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    // Skip recipient validation in test mode for test recipients only.
    // `import.meta.env.DEV` lets Vite tree-shake the bypass out of prod bundles (matches registration.ts gate).
    const isTestMode = import.meta.env.DEV && localStorage.getItem('__TEST_MODE__') === 'true';
    const isTestRecipient = recipient.startsWith('test');
    if (mode === 'send' && !recipientAccount && !(isTestMode && isTestRecipient)) {
      setLoginError('Recipient not found');
      return;
    }

    // Validate escrow fields if enabled
    if (escrowEnabled) {
      const price = parseFloat(escrowPrice);
      if (isNaN(price) || price <= 0) {
        setLoginError('Please enter a valid price');
        return;
      }
      const expiry = parseInt(escrowExpiry, 10);
      if (isNaN(expiry) || expiry < 1 || expiry > 30) {
        setLoginError('Expiry must be between 1 and 30 days');
        return;
      }
    }

    setStep('confirm');
  };

  // Render login/form step (for encrypted modes)
  const renderForm = () => {
    // If user is already logged in
    if (currentAccount) {
      // For store mode, skip directly to confirm (shouldn't reach here normally)
      if (mode === 'store') {
        setStep('confirm');
        return null;
      }

      // For send mode, show simplified form (recipient + optional escrow)
      return (
        <div className="content-centered">
          <h1 className="heading-lg">Select Recipient</h1>
          <p style={{ marginTop: 8, marginBottom: 32, opacity: 0.8 }}>
            Sending as {currentAccount.subdomain}.{ENS_DOMAIN}
          </p>

          <form onSubmit={handleRecipientSubmit} style={{ width: '100%', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
            <Input
              label="Recipient*"
              placeholder="username"
              value={recipient}
              onChange={e => handleRecipientChange(e.target.value)}
              autoFocus
            />
            {recipient && (
              <p style={{ fontSize: 14, marginTop: -8, marginBottom: 16, textAlign: 'right', color: isLookingUp ? 'inherit' : (recipientFound ? 'inherit' : '#e74c3c') }}>
                {isLookingUp ? 'Checking...' : (recipientFound ? 'Account found!' : 'Account not found')}
              </p>
            )}

            {loginError && (
              <p style={{ color: '#e74c3c', fontSize: 14, textAlign: 'center', marginBottom: 16 }}>{loginError}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 24 }}>
              <Button type="submit" variant="outline">
                {escrowEnabled ? 'Create Escrow' : 'Continue'}
              </Button>
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={() => navigate('/')}
              >
                <span style={{ fontSize: 14 }}>✕</span> Cancel
              </button>
            </div>
          </form>
        </div>
      );
    }

    // Not logged in - show full login form
    return (
      <div className="content-centered">
        <h1 className="heading-lg">Log in to encrypt</h1>

        <Button
          variant="outline"
          style={{ marginTop: 24, marginBottom: 16 }}
          onClick={() => navigate('/account')}
        >
          Sign in with Wallet
        </Button>

        <p style={{ marginBottom: 24, opacity: 0.8 }}>or</p>

        <form onSubmit={handleSubmitForm} style={{ width: '100%', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
          {mailboxOptions.length === 0 ? (
            <p style={{ textAlign: 'center', marginBottom: 16 }}>
              No accounts found.{' '}
              <a href="/account" style={{ color: 'inherit', textDecoration: 'underline' }}>Create one first</a>
            </p>
          ) : (
            <Select
              label="Select account"
              options={mailboxOptions}
              value={selectedMailbox}
              onChange={e => setSelectedMailbox(e.target.value)}
              placeholder="Select an account"
            />
          )}

          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />

          {mode === 'send' && (
            <>
              <Input
                label="Select recipient*"
                placeholder="username"
                value={recipient}
                onChange={e => handleRecipientChange(e.target.value)}
              />
              {recipient && (
                <p style={{ fontSize: 14, marginTop: -8, marginBottom: 16, textAlign: 'right', color: isLookingUp ? 'inherit' : (recipientFound ? 'inherit' : '#e74c3c') }}>
                  {isLookingUp ? 'Checking...' : (recipientFound ? 'Account found!' : 'Account not found')}
                </p>
              )}
            </>
          )}

          {loginError && (
            <p style={{ color: '#e74c3c', fontSize: 14, textAlign: 'center', marginBottom: 16 }}>{loginError}</p>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 24 }}>
            <Button type="submit" variant="outline" disabled={mailboxOptions.length === 0}>
              {escrowEnabled ? 'Create Escrow' : mode === 'send' ? 'Send File' : 'Store File'}
            </Button>
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => navigate('/')}
            >
              <span style={{ fontSize: 14 }}>✕</span> Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Render confirm step
  const renderConfirm = () => (
    <div className="content-centered">
      <h1 className="heading-lg">
        {mode === 'quick' ? 'Quick Share' : escrowEnabled ? 'Create Escrow' : 'Confirm'}
      </h1>
      {mode === 'quick' && (
        <p className="text-secondary" style={{ fontSize: 18, marginTop: 8 }}>
          Share without encryption
        </p>
      )}
      {escrowEnabled && (
        <p className="text-secondary" style={{ fontSize: 18, marginTop: 8 }}>
          Sell encrypted file with escrow protection
        </p>
      )}

      <div className="confirm-details" style={{
        width: '100%',
        maxWidth: 500,
        marginTop: 32,
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <div className="detail-row">
          <span style={{ opacity: 0.7 }}>File name</span>
          <span>{displayFile?.name}</span>
        </div>
        <div className="detail-row">
          <span style={{ opacity: 0.7 }}>Size</span>
          <span>{displayFile ? formatFileSize(displayFile.size) : ''}</span>
        </div>
        {mode === 'quick' && (
          <div className="detail-row">
            <span style={{ opacity: 0.7 }}>Encryption</span>
            <span>None (public link)</span>
          </div>
        )}
        {isEncrypted && (
          <>
            <div className="detail-row">
              <span style={{ opacity: 0.7 }}>Sender</span>
              <span>{selectedMailbox}.{ENS_DOMAIN}</span>
            </div>
            {mode === 'send' && (
              <div className="detail-row">
                <span style={{ opacity: 0.7 }}>Recipient</span>
                <span>{recipient}.{ENS_DOMAIN}</span>
              </div>
            )}
          </>
        )}
        {escrowEnabled && (
          <>
            <div className="detail-row">
              <span style={{ opacity: 0.7 }}>Price</span>
              <span>{escrowPrice} ETH</span>
            </div>
            <div className="detail-row">
              <span style={{ opacity: 0.7 }}>Expiry</span>
              <span>{escrowExpiry} days</span>
            </div>
            <div className="detail-row">
              <span style={{ opacity: 0.7 }}>Network</span>
              <span>{SUPPORTED_CHAINS.find(c => c.id === DEFAULT_ESCROW_CHAIN_ID)?.name || 'Sepolia'}</span>
            </div>
          </>
        )}
      </div>

      {uploadError && (
        uploadError.startsWith('STAMP_ERROR:') ? (
          <div className="error-display error-display--stamp">
            <h2 className="error-display-title">Postage Stamp Full</h2>
            <p className="error-display-message">
              {uploadError.replace('STAMP_ERROR:', '')}
            </p>
            <div className="error-display-instructions">
              <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>How to create a new stamp:</p>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>Install Swarm Desktop: <a href="https://desktop.ethswarm.org" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>desktop.ethswarm.org</a></li>
                <li>Fund your Bee node with BZZ tokens</li>
                <li>Purchase a postage stamp in the Swarm Desktop app</li>
                <li>Your local stamp will be used automatically for uploads</li>
              </ol>
            </div>
          </div>
        ) : (
          <ErrorDisplay
            error={uploadError}
            onDismiss={() => setUploadError(null)}
          />
        )
      )}

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 32 }}>
        <Button variant="outline" onClick={() => { setUploadError(null); startUpload(); }}>
          {uploadError ? 'Retry Upload' : (mode === 'quick' ? 'Upload' : escrowEnabled ? 'Create Escrow' : mode === 'send' ? 'Encrypt and Send' : 'Encrypt and Store')}
        </Button>
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          onClick={() => navigate('/')}
        >
          <span style={{ fontSize: 14 }}>✕</span> Cancel
        </button>
      </div>
    </div>
  );

  // Render uploading step
  const renderUploading = () => (
    <div className="content-centered">
      <h2 style={{ fontSize: 28 }}>Uploading</h2>
      <p style={{ marginTop: 16, fontSize: 18 }}>
        {displayFile?.name} <span style={{ opacity: 0.6 }}>{displayFile ? formatFileSize(displayFile.size) : ''}</span>
      </p>
      <p style={{ marginTop: 24, fontSize: 48, fontWeight: 500 }}>
        {Math.min(100, Math.round(progress))}%
      </p>
    </div>
  );

  // Render success step
  const renderSuccess = () => (
    <div className="content-centered" style={{ color: 'var(--white)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <CheckIcon style={{ width: 36, height: 36, display: 'block', flexShrink: 0 }} />
        <h2 style={{ fontSize: 32, lineHeight: 1, margin: 0 }}>
          {escrowId ? 'Escrow Created.' : 'Sent.'}
        </h2>
      </div>

      <p style={{ marginTop: 24, fontSize: 18 }}>
        {displayFile?.name} <span style={{ opacity: 0.6 }}>{displayFile ? formatFileSize(displayFile.size) : ''}</span>
      </p>

      {isEncrypted && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 }}>
          <LockIcon />
          <span>Encrypted</span>
        </div>
      )}

      {/* Escrow details */}
      {escrowId && (
        <div style={{
          marginTop: 24,
          padding: '16px 24px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 500,
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ opacity: 0.7 }}>Escrow ID</span>
            <span style={{ fontFamily: 'monospace' }}>{escrowId}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ opacity: 0.7 }}>Price</span>
            <span>{escrowPrice} ETH</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ opacity: 0.7 }}>Buyer</span>
            <span>{recipient}.{ENS_DOMAIN}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ opacity: 0.7 }}>Network</span>
            <span>{SUPPORTED_CHAINS.find(c => c.id === DEFAULT_ESCROW_CHAIN_ID)?.name || 'Sepolia'}</span>
          </div>
          <p style={{ fontSize: 12, opacity: 0.6, marginTop: 12, textAlign: 'center' }}>
            Waiting for buyer to fund the escrow
          </p>
        </div>
      )}

      <div style={{ marginTop: 32, width: '100%', maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>
        <p style={{ marginBottom: 12, fontSize: 14 }}>File Download Link</p>
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 'var(--radius-pill)',
          padding: '14px 20px',
          textAlign: 'center',
          wordBreak: 'break-all'
        }}>
          <span style={{ fontSize: 14 }}>{downloadLink}</span>
        </div>
        <button
          onClick={() => copy(downloadLink, 'link')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--white)',
            textDecoration: 'underline',
            cursor: 'pointer',
            marginTop: 12,
            fontSize: 14
          }}
        >
          {isCopied('link') ? 'Copied!' : 'Copy link.'}
        </button>
      </div>

      <div style={{ marginTop: 32 }}>
        <p style={{ fontSize: 14, marginBottom: 8 }}>Swarm Hash</p>
        <p style={{ fontSize: 14 }}>
          {shortenHash(swarmHash)}{' '}
          <button
            onClick={() => copy(swarmHash, 'hash')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--white)',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            {isCopied('hash') ? 'Copied!' : 'Copy'}
          </button>
        </p>
      </div>

      <button
        onClick={() => navigate('/')}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--white)',
          textDecoration: 'underline',
          cursor: 'pointer',
          marginTop: 32,
          fontSize: 14
        }}
      >
        Click to go to files.
      </button>
    </div>
  );

  // Determine theme based on step and mode
  const getTheme = () => {
    // Success is always dark
    if (step === 'success') return 'dark';
    // Everything else is green
    return 'green';
  };

  // Render current step content
  const renderContent = () => {
    switch (step) {
      case 'select': return renderSelectFile();
      case 'form': return renderForm();
      case 'confirm': return renderConfirm();
      case 'uploading': return renderUploading();
      case 'success': return renderSuccess();
      default: return renderSelectFile();
    }
  };

  return (
    <Layout theme={getTheme()}>
      {renderContent()}
      <WizardSteps
        steps={mode === 'quick' ? quickShareSteps : mode === 'store' ? storeSteps : undefined}
        currentStep={getWizardStep()}
      />
    </Layout>
  );
}

// Helper function
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
